/**
 * What the console has done lately, and how each one ended — GEN-41.
 *
 * Every mutation reported itself differently, and several not at all: an `Alert` here, a
 * `LinearProgress` there, a note with four states, and a re-pick that disabled its button
 * and refreshed. So a refused save and a save that worked could look the same, and an
 * operator who looked away had no way to catch up.
 *
 * The state is a plain reducer rather than a store, kept here so it is testable without a
 * renderer — the ordering and the cap are the parts worth asserting.
 */

export type ActionState = 'running' | 'done' | 'refused';

export interface Action {
  id: number;
  /** What was asked for, in the operator's words: "Save title", not "updateLesson". */
  name: string;
  state: ActionState;
  /** Present only on `refused`, and it is the API's own reason. */
  reason?: string;
}

/**
 * How many are kept. Enough to answer "what did I just do" after looking away, short
 * enough that the feed is read rather than scrolled.
 */
export const KEPT = 8;

export type ActionEvent =
  | { kind: 'started'; id: number; name: string }
  | { kind: 'settled'; id: number; reason?: string }
  | { kind: 'dismissed'; id: number };

/**
 * Newest first, capped, and an unknown id is ignored rather than appended.
 *
 * Ignoring is deliberate: a settle whose start has already aged out of the list is the
 * normal end of a long action, not an error, and appending it would put an action on the
 * feed with no beginning.
 */
export function reduceActions(
  actions: readonly Action[],
  event: ActionEvent,
): Action[] {
  if (event.kind === 'dismissed')
    return actions.filter((action) => action.id !== event.id);

  if (event.kind === 'started') {
    const fresh: Action = {
      id: event.id,
      name: event.name,
      state: 'running',
    };

    // A retry supersedes the refusal it is retrying — GEN-65. Without this the feed grew
    // an alert per attempt and never lost one, so the third try sat under two stale
    // copies of the reason it had already been given.
    const superseded = actions.filter(
      (action) => !(action.state === 'refused' && action.name === event.name),
    );

    return [fresh, ...superseded].slice(0, KEPT);
  }

  return actions.map((action) =>
    action.id === event.id
      ? {
          ...action,
          state: event.reason === undefined ? 'done' : 'refused',
          ...(event.reason === undefined ? {} : { reason: event.reason }),
        }
      : action,
  );
}

/** Whether anything is still in flight, which is what the shell shows a bar for. */
export const anyRunning = (actions: readonly Action[]): boolean =>
  actions.some((action) => action.state === 'running');
